import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { StaffTicketStats } from "@signalist/shared-types";

import { classNames } from "@/utils/classNames";

import {
	ageParts,
	autoCloseWindowDays,
	DurationParts,
	durationParts,
} from "../utils/staffTicketFormat";

const Tile: FunctionComponent<{
	id: string;
	label: string;
	value: ReactNode;
	note?: string | null;
	hot?: boolean;
}> = ({ id, label, value, note, hot }) => (
	<div className="flex min-w-0 flex-1 flex-col gap-0.5 px-4 py-2.5" data-testid={`tile-${id}`}>
		<span className="truncate text-[11px] font-semibold uppercase tracking-wide text-text-meta">
			{label}
		</span>
		<span className="flex items-baseline gap-2">
			<span
				className={classNames("text-lg font-semibold", hot ? "text-status-error" : "text-text")}
			>
				{value}
			</span>
			{note && <span className="truncate text-xs text-text-meta">{note}</span>}
		</span>
	</div>
);

/**
 * The triage strip (mockup `.strip`): whole-queue numbers from `GET tickets/admin/stats`, never
 * narrowed by the filter row. A tile shows "—" until its number is there.
 */
export const TicketTriageStrip: FunctionComponent<{ stats?: StaffTicketStats; now?: Date }> = ({
	stats,
	now = new Date(),
}) => {
	const { t } = useTranslation();

	const noData = t("support.staff.strip.noData");
	const unit = (parts: DurationParts) =>
		t(`support.staff.units.${parts.unit}`, { value: parts.value });
	const count = (value: number | undefined) =>
		value === undefined ? noData : String(Number(value) || 0);

	const oldest = stats?.waitingOnUs.oldestCustomerActivityAt
		? t("support.staff.strip.oldest", {
				age: unit(ageParts(stats.waitingOnUs.oldestCustomerActivityAt, now)),
			})
		: null;
	const autoClose = stats
		? autoCloseWindowDays(
				stats.resolvedAwaitingClose.earliestAutoCloseAt,
				stats.resolvedAwaitingClose.latestAutoCloseAt,
				now
			)
		: null;
	const median = stats?.medianFirstReplyMinutes ?? null;

	return (
		<div
			className="mb-3 flex divide-x divide-border-light overflow-hidden rounded-[10px] border border-border-light bg-white"
			data-testid="triage-strip"
		>
			<Tile
				hot
				id="waitingOnUs"
				label={t("support.staff.strip.waitingOnUs")}
				note={oldest}
				value={count(stats?.waitingOnUs.count)}
			/>
			<Tile
				id="openUntriaged"
				label={t("support.staff.strip.openUntriaged")}
				value={count(stats?.openUntriaged)}
			/>
			<Tile
				id="unassigned"
				label={t("support.staff.strip.unassigned")}
				value={count(stats?.unassigned)}
			/>
			<Tile
				id="waitingOnCustomer"
				label={t("support.staff.strip.waitingOnCustomer")}
				value={count(stats?.waitingOnCustomer)}
			/>
			<Tile
				id="resolvedAwaitingClose"
				label={t("support.staff.strip.resolvedAwaitingClose")}
				note={
					autoClose
						? t("support.staff.strip.autoCloseIn", {
								window: t("support.staff.units.DAY", { value: autoClose }),
							})
						: null
				}
				value={count(stats?.resolvedAwaitingClose.count)}
			/>
			<Tile
				id="medianFirstReply"
				label={t("support.staff.strip.medianFirstReply")}
				note={t("support.staff.strip.last30Days")}
				value={median === null ? noData : unit(durationParts(median))}
			/>
		</div>
	);
};
